import React from 'react'
import PropTypes from 'prop-types'

import useVibemapSearch from '../utils/search-context/hook'
import Fuse from 'fuse.js'

import CircularProgress from '@mui/material/CircularProgress'

import './styles/Field.scss'


/** @typedef {"place"|"event"|"guide"|"city"} ItemType */

/**
 * @typedef {Object} Item
 * @property {String} url
 * @property {ItemType} type
 * @property {String} label
 */

/**
 * @param {Object} item
 * @param {ItemType} type
 * @returns {String} label
 */
function getItemLabel(item, type) {
  switch (type) {
    case 'place':
      return item.properties.name;
    case 'event':
      return item.properties.name;
    case 'city':
      return item.title;
    case 'guide':
      return item.title.rendered
    default:
      throw new Error(`Cannot get item label: Invalid item type '${type}'`)
  }
}

/**
 * @param {Object} item
 * @param {ItemType} type
 * @returns {String} URL
 */
function getItemURL(item, type) {
  switch (type) {
    case 'place':
      return `https://app.vibemap.com/details/${item.id}`
    case 'event':
      return `https://app.vibemap.com/events/${item.id}`
    case 'city':
      return item.uri;
    case 'guide':
      return item.link
    default:
      throw new Error(`Cannot get item URL: Invalid item type '${type}'`)
  }
}

/**
 * @param {Object} item
 * @param {ItemType} type
 * @returns {Item} formattedItem
 */
function makeFormattedItem(item, type) {
  return {
    url: getItemURL(item, type),
    label: getItemLabel(item, type),
    subLabel: type === 'place' ? item.properties.address : null,
    details: item
  }
}

/**
 * @prop {title} string
 * @prop {Array<Item>} items
 */
function VibemapSearchAutoCompleteSectionView({
  // Props
  title,
  items,
  onClick,
  onUpdate,
  resultsPerSection,
  showLink,
  showCities,
  showEvents,
  showGuides,
  showPlaces
}) {
  const limitedItems = items.slice(0, resultsPerSection)
  return (
    <div className='autocomplete__section'>
      <div className='section__header'>
        <h6 className='section__title'>
          {title}
        </h6>
      </div>
      <div className='section__option-container'>
        {limitedItems.map((item) => (
          <div
            className='section__option'
            key={item.url}
            onClick={(e) => onClick(e, item)}
          >
            {showLink
              ? <a href={item.url}>{item.label}</a>
              : <><span className='name'>{item.label}</span> {item.subLabel ? <span className='sublabel'>{item.subLabel}</span> : null}</>
            }
          </div>
        ))}
      </div>
    </div>
  )
}

const VibemapSearchAutoCompleteSection = React.memo(VibemapSearchAutoCompleteSectionView)

function VibemapSearchAutoComplete({
  onClick,
  onUpdate = () => { },
  isOpen,
  showLink,
  showCities,
  showEvents,
  showGuides,
  showPlaces,
  threshold
}) {
  const {
    progress,
    searchTerm,
    results
  } = useVibemapSearch()

  const FUSE_OPTIONS = {
    keys: ['label', 'subLabel'],
    threshold: threshold,
  }

  // Is anything loading
  const isLoading = Object.values(progress).includes(true)

  const formattedResults = React.useMemo(() => {
    const allResults = {
      cities: results.cities.map((city) => makeFormattedItem(city, 'city')),
      events: results.events.map((event) => makeFormattedItem(event, 'event')),
      places: results.places.map((place) => makeFormattedItem(place, 'place')),
      guides: results.guides.map((guide) => makeFormattedItem(guide, 'guide')),
    }

    const hasResults = (
      allResults?.cities && allResults?.cities?.length > 0 ||
      allResults?.events && allResults?.events?.length > 0 ||
      allResults?.places && allResults?.places?.length > 0 ||
      allResults?.guides && allResults?.guides?.length > 0
    )

    if (hasResults) {
      onUpdate(allResults)
    }

    return allResults
  }, [results, results[0]])

  const cityFuse = React.useMemo(
    () => new Fuse(formattedResults.cities, FUSE_OPTIONS),
    [formattedResults]
  )

  const placeFuse = React.useMemo(
    () => new Fuse(formattedResults.places, FUSE_OPTIONS),
    [formattedResults]
  )

  const guideFuse = React.useMemo(
    () => new Fuse(formattedResults.guides, FUSE_OPTIONS),
    [formattedResults]
  )
  const eventFuse = React.useMemo(
    () => new Fuse(formattedResults.events, FUSE_OPTIONS),
    [formattedResults]
  )

  const fuzzyCityResults = React.useMemo(
    () => searchTerm
      ? cityFuse.search(searchTerm).map(({ item }) => item)
      : [],
    [cityFuse, searchTerm]
  )
  const fuzzyPlaceResults = React.useMemo(
    () => searchTerm
      ? placeFuse.search(searchTerm).map(({ item }) => item)
      : [],
    [placeFuse, searchTerm]
  )
  const fuzzyGuideResults = React.useMemo(
    () => searchTerm
      ? guideFuse.search(searchTerm).map(({ item }) => item)
      : [],
    [guideFuse, searchTerm]
  )
  const fuzzyEventResults = React.useMemo(
    () => searchTerm
      ? eventFuse.search(searchTerm).map(({ item }) => item)
      : [],
    [eventFuse, searchTerm]
  )

  if (!searchTerm) return null;
  if (!isOpen) return null;

  if (
    !(
      fuzzyCityResults?.length ||
      fuzzyPlaceResults?.length ||
      fuzzyEventResults?.length ||
      fuzzyGuideResults?.length
    )
  ) {
    console.log(`No fuzzy results for '${searchTerm}'`)
    return null;
  }

  return (
    <div className='autocomplete'>
      <h5 className="autocomplete__title">Suggestions</h5>
      {isLoading && <CircularProgress color="secondary" size={20} style={{ position: 'absolute', right: '4px' }} />}
      {showPlaces && fuzzyPlaceResults?.length > 0 && (
        <VibemapSearchAutoCompleteSection
          title="Places"
          items={fuzzyPlaceResults}
          onClick={onClick}
          onUpdate={onUpdate}
          showLink={showLink}
        />
      )}
      {showEvents && fuzzyEventResults?.length > 0 && (
        <VibemapSearchAutoCompleteSection
          title="Events"
          items={fuzzyEventResults}
          onClick={onClick}
          showLink={showLink}
        />
      )}
      {showCities && fuzzyCityResults?.length > 0 && (
        <VibemapSearchAutoCompleteSection
          title="Cities"
          items={fuzzyCityResults}
          onClick={onClick}
          showLink={showLink}
        />
      )}
      {showGuides && fuzzyGuideResults?.length > 0 && (
        <VibemapSearchAutoCompleteSection
          title="Guides"
          items={fuzzyGuideResults}
          onClick={onClick}
          showLink={showLink}
        />
      )}
    </div>
  )
}

VibemapSearchAutoComplete.propTypes = {
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onSubmit: PropTypes.func,
  resultsPerSection: PropTypes.number,
  showLink: PropTypes.bool,
  showCities: PropTypes.bool,
  showPlaces: PropTypes.bool,
  showEvents: PropTypes.bool,
  showGuides: PropTypes.bool,
}

VibemapSearchAutoComplete.defaultProps = {
  showCities: true,
  showPlaces: true,
  showEvents: true,
  showGuides: true,
  resultsPerSection: 3,
  showLink: false,
  threshold: 0.5
}

//VibemapSearchAutoComplete.whyDidYouRender = true
export default VibemapSearchAutoComplete
